// src/features/mensagens/api/templates.js
// ============================================================================
// 📋 CATÁLOGO DE PLANTILLAS DE MENSAJE — ÚNICA FUENTE DE VERDAD
// 📦 V2 ENTERPRISE — White-Label · Empresa Dinámica · WhatsApp-Ready
// ============================================================================
//
// REGLAS DE ORO:
// 1. TODAS las plantillas del sistema se definen AQUÍ
// 2. NINGÚN componente define sus propias plantillas
// 3. `MESSAGE_TEMPLATES` en constants.js es el CONTRATO (nombres)
// 4. Este archivo es la IMPLEMENTACIÓN (datos, categorías, metadata)
// 5. Cuando integres WhatsApp API, las plantillas oficiales van AQUÍ
// 6. 🔥 V2: NUNCA hardcodear nombre de empresa — usar parámetro `empresa`
// 7. 🔥 V2: Fallback genérico "nossa equipe" si no hay empresa configurada
// ============================================================================

import { MESSAGE_TEMPLATES } from '../utils/constants';

// ============================================================================
// 🔥 V2: HELPER — Obtener nombre de empresa (con fallback)
// ============================================================================

/**
 * Obtiene el nombre de la empresa desde el contexto de autenticación
 * o usa un fallback genérico white-label
 * 
 * @returns {string} Nombre de la empresa
 */
function getEmpresaNome() {
  try {
    // Intentar obtener del contexto de autenticación
    if (typeof window !== 'undefined') {
      // Opción 1: localStorage (más rápido, sin dependencia circular)
      const authUser = localStorage.getItem('auth_user');
      if (authUser) {
        const parsed = JSON.parse(authUser);
        if (parsed?.company && typeof parsed.company === 'string' && parsed.company !== 'Imobiliária') {
          return parsed.company;
        }
      }

      // Opción 2: Base de datos local
      const db = localStorage.getItem('imoveispro_db');
      if (db) {
        const parsed = JSON.parse(db);
        const company = parsed?.company?.nome || parsed?.empresa?.nome || parsed?.users?.[0]?.company;
        if (company && company !== 'Imobiliária') {
          return company;
        }
      }

      // Opción 3: Configuración del SaaS
      const config = localStorage.getItem('saas_config') || localStorage.getItem('app_config');
      if (config) {
        const parsed = JSON.parse(config);
        if (parsed?.companyName || parsed?.empresa) {
          return parsed.companyName || parsed.empresa;
        }
      }
    }
  } catch (e) {
    console.warn('⚠️ [Templates] No se pudo obtener el nombre de la empresa:', e.message);
  }

  // 🔥 V2: Fallback genérico white-label (sin marcas hardcodeadas)
  return 'nossa equipe';
}

// ============================================================================
// 🎯 CATÁLOGO COMPLETO DE PLANTILLAS
// ============================================================================

/**
 * Cada plantilla tiene:
 * - id: Identificador único (coincide con MESSAGE_TEMPLATES keys)
 * - name: Nombre legible en portugués
 * - icon: Icono de Lucide React
 * - category: Categoría para agrupar
 * - color: Color Tailwind para el badge
 * - generate: Función que genera el texto (name, empresa?, property?)
 * - preview: Texto corto de preview
 * - tags: Etiquetas para búsqueda
 * - whatsappTemplate: Nombre en WhatsApp Business API (futuro)
 */

const TEMPLATES_CATALOG = [
  // --------------------------------------------------------------------------
  // 📞 ATENDIMENTO (Atención al cliente)
  // --------------------------------------------------------------------------
  {
    id: 'padrao',
    name: 'Atendimento Padrão',
    icon: 'MessageSquare',
    category: 'atendimento',
    color: 'bg-blue-500',
    preview: 'Olá, {nome}! Estou entrando em contato...',
    tags: ['olá', 'contato', 'primeiro', 'padrão', 'inicial'],
    whatsappTemplate: null,
    // 🔥 V2: empresa dinámico
    generate: (name = '', empresa = '') =>
      `Olá, ${name || 'Prezado(a) cliente'}! 😊\n\n` +
      `Estou entrando em contato da ${empresa || 'nossa equipe'}. Como posso te ajudar hoje?`,
  },

  {
    id: 'proposta',
    name: 'Nova Proposta',
    icon: 'FileText',
    category: 'negociacao',
    color: 'bg-green-500',
    preview: 'Olá, {nome}! Tenho uma proposta...',
    tags: ['proposta', 'negociação', 'interesse', 'valor'],
    whatsappTemplate: 'proposta_imovel_v1',
    // 🔥 V2: empresa dinámico
    generate: (name = '', empresa = '') =>
      `Olá, ${name || 'Prezado(a) cliente'}! 👋\n\n` +
      `Tenho uma proposta interessante da ${empresa || 'nossa equipe'} para você. Podemos conversar?`,
  },

  {
    id: 'visita',
    name: 'Agendar Visita',
    icon: 'Home',
    category: 'visitas',
    color: 'bg-purple-500',
    preview: 'Olá, {nome}! Gostaria de agendar...',
    tags: ['visita', 'agendamento', 'imóvel', 'tour', 'presencial'],
    whatsappTemplate: 'agendar_visita_v1',
    // 🔥 V2: empresa dinámico
    generate: (name = '', empresa = '') =>
      `Olá, ${name || 'Prezado(a) cliente'}! 🏠\n\n` +
      `Gostaria de agendar uma visita para o imóvel que você demonstrou interesse. ` +
      `Qual seria o melhor horário para você?`,
  },

  {
    id: 'lembrete',
    name: 'Lembrete de Follow-up',
    icon: 'Bell',
    category: 'followup',
    color: 'bg-amber-500',
    preview: 'Olá, {nome}! Passando para lembrar...',
    tags: ['lembrete', 'follow-up', 'acompanhamento', 'recordar'],
    whatsappTemplate: 'lembrete_followup_v1',
    // 🔥 V2: empresa dinámico
    generate: (name = '', empresa = '') =>
      `Olá, ${name || 'Prezado(a) cliente'}! ⏰\n\n` +
      `Passando para lembrar da nossa conversa sobre imóveis. ` +
      `Fico à disposição para qualquer dúvida!`,
  },

  // --------------------------------------------------------------------------
  // 🏠 IMÓVEIS (Relacionados a propiedades)
  // --------------------------------------------------------------------------
  {
    id: 'novo_imovel',
    name: 'Novo Imóvel Disponível',
    icon: 'PlusSquare',
    category: 'imoveis',
    color: 'bg-indigo-500',
    preview: 'Acabou de chegar! {código} - {título}...',
    tags: ['novo', 'imóvel', 'lançamento', 'disponível'],
    whatsappTemplate: null,
    // 🔥 V2: empresa dinámico
    generate: (name = '', empresa = '', property = {}) => {
      const title = property.title || property.code || 'novo imóvel';
      const price = property.price ? ` por R$ ${property.price.toLocaleString('pt-BR')}` : '';
      return (
        `Olá, ${name || 'Prezado(a) cliente'}! 🎉\n\n` +
        `Acabou de chegar na ${empresa || 'nossa equipe'}: *${title}*${price}!\n\n` +
        `Se tiver interesse, posso enviar mais detalhes e agendar uma visita. 😊`
      );
    },
  },

  {
    id: 'reducao_preco',
    name: 'Redução de Preço',
    icon: 'TrendingDown',
    category: 'imoveis',
    color: 'bg-red-500',
    preview: 'Boa notícia! O imóvel {código} teve redução...',
    tags: ['redução', 'preço', 'desconto', 'promoção', 'oportunidade'],
    whatsappTemplate: null,
    // 🔥 V2: empresa dinámico
    generate: (name = '', empresa = '', property = {}) => {
      const title = property.title || property.code || 'um imóvel';
      const oldPrice = property.oldPrice ? ` de R$ ${property.oldPrice.toLocaleString('pt-BR')}` : '';
      const newPrice = property.price ? ` para R$ ${property.price.toLocaleString('pt-BR')}` : '';
      return (
        `Olá, ${name || 'Prezado(a) cliente'}! 📉\n\n` +
        `Boa notícia! O imóvel *${title}* teve redução de preço${oldPrice}${newPrice}!\n\n` +
        `Essa oportunidade não vai durar muito. Quer saber mais?`
      );
    },
  },

  // --------------------------------------------------------------------------
  // 📄 DOCUMENTAÇÃO
  // --------------------------------------------------------------------------
  {
    id: 'solicitar_documentos',
    name: 'Solicitar Documentos',
    icon: 'FileCheck',
    category: 'documentacao',
    color: 'bg-teal-500',
    preview: 'Para prosseguir, precisamos dos seguintes...',
    tags: ['documentos', 'documentação', 'papelada', 'contrato'],
    whatsappTemplate: 'solicitar_documentos_v1',
    // 🔥 V2: empresa dinámico
    generate: (name = '', empresa = '') =>
      `Olá, ${name || 'Prezado(a) cliente'}! 📋\n\n` +
      `Para dar continuidade ao processo, precisamos dos seguintes documentos:\n` +
      `• RG e CPF\n` +
      `• Comprovante de residência\n` +
      `• Comprovante de renda\n\n` +
      `Pode enviar por aqui mesmo. Obrigado!`,
  },

  {
    id: 'confirmacao_recebimento',
    name: 'Confirmação de Recebimento',
    icon: 'CheckCircle',
    category: 'documentacao',
    color: 'bg-emerald-500',
    preview: 'Recebemos sua documentação! Está em...',
    tags: ['confirmação', 'recebimento', 'documentos', 'ok'],
    whatsappTemplate: null,
    // 🔥 V2: empresa dinámico
    generate: (name = '', empresa = '') =>
      `Olá, ${name || 'Prezado(a) cliente'}! ✅\n\n` +
      `Recebemos sua documentação. Ela está em análise e retornaremos em breve com a devolutiva.\n\n` +
      `Obrigado pela agilidade!`,
  },

  // --------------------------------------------------------------------------
  // 🎉 PÓS-VENDA
  // --------------------------------------------------------------------------
  {
    id: 'pos_visita',
    name: 'Pós-Visita',
    icon: 'MessageCircle',
    category: 'pos_venda',
    color: 'bg-cyan-500',
    preview: 'E aí, o que achou da visita ao imóvel?',
    tags: ['pós-visita', 'feedback', 'opinião', 'impressão'],
    whatsappTemplate: null,
    // 🔥 V2: empresa dinámico
    generate: (name = '', empresa = '') =>
      `Olá, ${name || 'Prezado(a) cliente'}! 🏠\n\n` +
      `E aí, o que achou da visita ao imóvel? Ficou com alguma dúvida?\n\n` +
      `Estou aqui para ajudar no que precisar!`,
  },

  {
    id: 'aniversario',
    name: 'Feliz Aniversário',
    icon: 'Cake',
    category: 'relacionamento',
    color: 'bg-pink-500',
    preview: 'Feliz aniversário! Muitas felicidades...',
    tags: ['aniversário', 'parabéns', 'felicidades', 'relacionamento'],
    whatsappTemplate: 'aniversario_cliente_v1',
    // 🔥 V2: empresa dinámico — "Imóveis Pro" hardcodeado ELIMINADO
    generate: (name = '', empresa = '') =>
      `Olá, ${name || 'Prezado(a) cliente'}! 🎂\n\n` +
      `A equipe ${empresa || 'nossa equipe'} deseja um *feliz aniversário*! ` +
      `Muitas felicidades, saúde e realizações. É um prazer tê-lo(a) conosco! 🎉`,
  },
];

// ============================================================================
// 📂 CATEGORÍAS DE PLANTILLAS
// ============================================================================

const TEMPLATE_CATEGORIES = Object.freeze([
  { id: 'atendimento',    name: 'Atendimento',    icon: 'Headphones',    color: 'bg-blue-100 text-blue-700' },
  { id: 'negociacao',     name: 'Negociação',     icon: 'Handshake',     color: 'bg-green-100 text-green-700' },
  { id: 'visitas',        name: 'Visitas',        icon: 'CalendarCheck', color: 'bg-purple-100 text-purple-700' },
  { id: 'followup',       name: 'Follow-up',      icon: 'RefreshCw',     color: 'bg-amber-100 text-amber-700' },
  { id: 'imoveis',        name: 'Imóveis',        icon: 'Building',      color: 'bg-indigo-100 text-indigo-700' },
  { id: 'documentacao',   name: 'Documentação',   icon: 'FileText',      color: 'bg-teal-100 text-teal-700' },
  { id: 'pos_venda',      name: 'Pós-Venda',      icon: 'Heart',         color: 'bg-cyan-100 text-cyan-700' },
  { id: 'relacionamento', name: 'Relacionamento', icon: 'Star',          color: 'bg-pink-100 text-pink-700' },
]);

// ============================================================================
// 🎯 API PÚBLICA
// ============================================================================

/**
 * Obtiene todas las plantillas disponibles
 * @param {Object} options - Opciones de filtrado
 * @param {string} [options.category] - Filtrar por categoría
 * @param {string} [options.search] - Buscar por nombre o tags
 * @returns {Array} Plantillas filtradas
 */
export function getTemplates({ category = null, search = null } = {}) {
  let templates = [...TEMPLATES_CATALOG];

  // Filtrar por categoría
  if (category) {
    templates = templates.filter(t => t.category === category);
  }

  // Buscar por nombre o tags
  if (search) {
    const term = search.toLowerCase().trim();
    templates = templates.filter(t =>
      t.name.toLowerCase().includes(term) ||
      t.tags.some(tag => tag.includes(term)) ||
      t.category.includes(term)
    );
  }

  return templates;
}

/**
 * Obtiene una plantilla por su ID
 * @param {string} id - ID de la plantilla
 * @returns {Object|null} Plantilla encontrada o null
 */
export function getTemplateById(id) {
  return TEMPLATES_CATALOG.find(t => t.id === id) || null;
}

/**
 * 🔥 V2: Genera el texto de una plantilla con nombre de empresa dinámico
 * 
 * @param {string} id - ID de la plantilla
 * @param {Object} data - Datos para personalizar
 * @param {string} [data.name] - Nombre del cliente
 * @param {string} [data.empresa] - Nombre de la empresa (opcional, se auto-detecta)
 * @param {Object} [data.property] - Datos de la propiedad (opcional)
 * @returns {string} Texto generado
 */
export function generateTemplateMessage(id, { name = '', empresa = null, property = null } = {}) {
  const template = getTemplateById(id);
  if (!template) {
    console.warn(`⚠️ [Templates] Plantilla no encontrada: ${id}`);
    return '';
  }

  // 🔥 V2: Resolver nombre de empresa
  // Prioridad: 1) Parámetro explícito, 2) Contexto de auth, 3) Fallback
  const empresaNome = empresa || getEmpresaNome();

  // 🔥 V2: Pasar empresa como segundo argumento a generate()
  return template.generate(name, empresaNome, property);
}

/**
 * Obtiene todas las categorías de plantillas
 * @returns {Array} Categorías
 */
export function getTemplateCategories() {
  return [...TEMPLATE_CATEGORIES];
}

/**
 * Obtiene plantillas agrupadas por categoría
 * @returns {Object} { categoryId: [templates] }
 */
export function getTemplatesByCategory() {
  const grouped = {};
  for (const category of TEMPLATE_CATEGORIES) {
    grouped[category.id] = TEMPLATES_CATALOG.filter(t => t.category === category.id);
  }
  return grouped;
}

/**
 * Busca plantillas por texto (nombre, tags, categoría)
 * @param {string} query - Texto de búsqueda
 * @returns {Array} Plantillas encontradas
 */
export function searchTemplates(query) {
  return getTemplates({ search: query });
}

/**
 * Verifica si una plantilla requiere aprobación de WhatsApp Business API
 * @param {string} id - ID de la plantilla
 * @returns {boolean}
 */
export function requiresWhatsAppApproval(id) {
  const template = getTemplateById(id);
  return !!template?.whatsappTemplate;
}

/**
 * Obtiene el nombre de plantilla en WhatsApp Business API
 * @param {string} id - ID de la plantilla interna
 * @returns {string|null} Nombre en WhatsApp API o null si es texto libre
 */
export function getWhatsAppTemplateName(id) {
  const template = getTemplateById(id);
  return template?.whatsappTemplate || null;
}

// ============================================================================
// 🧊 CONGELAR
// ============================================================================
if (typeof Object.freeze === 'function') {
  Object.freeze(TEMPLATES_CATALOG);
  Object.freeze(TEMPLATE_CATEGORIES);
}

// ============================================================================
// 📤 EXPORTACIÓN DEFAULT
// ============================================================================

export default {
  getAll: getTemplates,
  getById: getTemplateById,
  generate: generateTemplateMessage,
  getCategories: getTemplateCategories,
  getByCategory: getTemplatesByCategory,
  search: searchTemplates,
  requiresApproval: requiresWhatsAppApproval,
  getWhatsAppName: getWhatsAppTemplateName,
};